import { ShopigurusHTMLElement } from '@shopigurus/base';

/**
 * ShpfltFloatingAtc — Floating circular add-to-cart button overlaid on product cards.
 * Single-variant products submit directly; multi-variant products open a quick-buy modal.
 *
 * @fires document#floating-atc:added
 * @fires documentElement#cart:change
 *
 * @example
 * <shpflt-floating-atc>
 *   <product-form>...</product-form>
 * </shpflt-floating-atc>
 */
class ShpfltFloatingAtc extends ShopigurusHTMLElement {
  /** @type {number | null} */
  #resetTimer = null;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.abortController.signal;

    const form = this.querySelector('product-form form');
    if (form) {
      // capture: true fires before theme.js ProductForm's bubble-phase submit listener.
      // stopImmediatePropagation inside #handleATC prevents ProductForm from running.
      form.addEventListener('submit', (e) => this.#handleATC(e), { signal, capture: true });
    } else {
      const btn = this.querySelector('.floating-atc-fab[aria-controls]');
      if (btn) {
        btn.addEventListener('click', () => this.#handleModalOpen(btn), { signal });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#resetTimer) clearTimeout(this.#resetTimer);
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  #handleModalOpen(btn) {
    const modalId = btn.getAttribute('aria-controls');
    const modal = this.querySelector(`#${modalId}`);
    if (modal && typeof modal.show === 'function') {
      modal.show();
    } else if (modal) {
      modal.dispatchEvent(new Event('click', { bubbles: true }));
    }
    btn.setAttribute('aria-expanded', 'true');
  }

  /**
   * @param {SubmitEvent} e
   * @fires document#floating-atc:added
   * @fires documentElement#cart:change
   */
  async #handleATC(e) {
    e.preventDefault();
    e.stopImmediatePropagation();
    const btn = this.querySelector('.floating-atc-fab');
    if (!btn || btn.classList.contains('is-busy')) return;
    btn.classList.add('is-busy');
    const busyStartedAt = performance.now();

    const form = this.querySelector('product-form form');
    const variantId = parseInt(form.querySelector('input[name="id"]').value, 10);

    // Ask cart drawer (and any other section) to register its section ID for re-rendering.
    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle },
    }));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: [...sectionsToBundle].join(','),
        }),
        signal: this.abortController.signal,
      });

      const responseJson = await res.json();

      // Guarantee the spinner stays visible long enough to perceive, even on fast connections.
      await this.#ensureMinimumBusyTime(busyStartedAt);

      if (res.ok) {
        btn.classList.remove('is-busy');
        btn.classList.add('is-success');
        await this.#dispatchCartEvents(responseJson);
        this.#scheduleReset(btn);
      } else {
        btn.classList.remove('is-busy');
        this.#dispatchCartError(responseJson, form);
      }
    } catch {
      await this.#ensureMinimumBusyTime(busyStartedAt);
      btn.classList.remove('is-busy');
    }
  }

  /**
   * @param {number} startedAt - performance.now() timestamp when .is-busy was added
   * @returns {Promise<void>}
   */
  #ensureMinimumBusyTime(startedAt) {
    const MIN_BUSY_MS = 500;
    const elapsed = performance.now() - startedAt;
    const remaining = MIN_BUSY_MS - elapsed;
    if (remaining <= 0) return Promise.resolve();
    return new Promise((resolve) => setTimeout(resolve, remaining));
  }

  /**
   * Mirrors theme.js ProductForm error handling so the toast and cart refresh still fire.
   * @param {Object} responseJson
   * @param {HTMLFormElement} form
   * @fires form#cart:error
   * @fires document#toast:show
   * @fires documentElement#cart:refresh
   */
  #dispatchCartError(responseJson, form) {
    const message = responseJson?.message || responseJson?.description || '';
    form.dispatchEvent(new CustomEvent('cart:error', {
      bubbles: true,
      detail: { error: message },
    }));
    document.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message, tone: 'error' },
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /**
   * @param {Object} responseJson - Parsed /cart/add.js response (contains sections HTML)
   * @fires document#floating-atc:added
   * @fires documentElement#cart:change
   */
  async #dispatchCartEvents(responseJson) {
    document.dispatchEvent(new CustomEvent('floating-atc:added', { bubbles: true }));

    // Mirror theme.js ProductForm success notifications (toast for cartType=message,
    // live-region announcement for cartType=drawer) — we stopped propagation, so we
    // must fire these ourselves.
    const items = responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson];
    const settings = window.themeVariables?.settings;
    const strings = window.themeVariables?.strings;
    if (strings?.addedToCart) {
      const messages = items.map((item) =>
        strings.addedToCart.replace('{{product_title}}', item?.title || '')
      );
      if (settings?.cartType === 'message') {
        document.dispatchEvent(new CustomEvent('toast:show', {
          detail: { message: messages[0] },
        }));
      } else if (settings?.cartType === 'drawer') {
        const region = document.getElementById('status-announcement');
        if (region) {
          region.textContent = '';
          setTimeout(() => { region.textContent = messages.join(', '); }, 100);
        }
      }
    }

    try {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal,
      });
      if (res.ok) {
        const cart = await res.json();
        // Attach pre-rendered section HTML so cart drawer can update without a second fetch.
        cart.sections = responseJson.sections;
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          // baseEvent: 'variant:add' triggers the drawer to open automatically.
          detail: { baseEvent: 'variant:add', cart },
        }));
      }
    } catch { /* silent — cart icon updates on next page load */ }
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  #scheduleReset(btn) {
    this.#resetTimer = setTimeout(() => {
      btn.classList.remove('is-busy', 'is-success');
    }, 1200);
  }
}

customElements.define('shpflt-floating-atc', ShpfltFloatingAtc);
